/**
 * Supported Protected-AI providers ("apps") and the concrete tools that roll
 * up under each — the frontend mirror of the API's ai-platforms registry
 * (lib/privacypal-api/server/utils/ai-platforms.js). The dashboard charts,
 * table and drill-down report all render from this list so every supported
 * AI is always shown, active or not.
 *
 * Chart colors are CVD-validated in this exact series order for both themes
 * (dataviz six-check validator, light surface #ffffff / dark #1e293b), so
 * keep the array order when stacking series.
 */

export interface AiTool {
  key: string;
  name: string;
}

export interface AiProvider {
  key: string;
  name: string;
  vendor: string;
  tools: AiTool[];
  /** Series color per theme (validated palette). */
  color: { light: string; dark: string };
  /** Tailwind helpers for pills/cards. */
  bgLight: string;
  textColor: string;
  borderColor: string;
}

export const AI_PROVIDERS: AiProvider[] = [
  {
    key: 'claude',
    name: 'Claude',
    vendor: 'Anthropic',
    tools: [
      { key: 'claude', name: 'Claude Chat' },
      { key: 'claude-code', name: 'Claude Code' },
      { key: 'claude-cowork', name: 'Claude Cowork' },
    ],
    color: { light: '#8b5cf6', dark: '#8b5cf6' },
    bgLight: 'bg-purple-50 dark:bg-purple-500/10',
    textColor: 'text-purple-600 dark:text-purple-400',
    borderColor: 'border-purple-200 dark:border-purple-500/20',
  },
  {
    key: 'chatgpt',
    name: 'ChatGPT',
    vendor: 'OpenAI',
    tools: [
      { key: 'chatgpt', name: 'ChatGPT' },
      { key: 'openai-api', name: 'OpenAI API' },
      // Mirrors the chatgpt provider's tools in the API's ai-platforms.js.
      { key: 'codex', name: 'Codex' },
    ],
    color: { light: '#10b981', dark: '#059669' },
    bgLight: 'bg-green-50 dark:bg-green-500/10',
    textColor: 'text-green-600 dark:text-green-400',
    borderColor: 'border-green-200 dark:border-green-500/20',
  },
  {
    key: 'gemini',
    name: 'Gemini',
    vendor: 'Google',
    tools: [
      { key: 'gemini', name: 'Gemini' },
      { key: 'google-ai-mode', name: 'Google AI Mode' },
    ],
    color: { light: '#4285f4', dark: '#4285f4' },
    bgLight: 'bg-blue-50 dark:bg-blue-500/10',
    textColor: 'text-blue-600 dark:text-blue-400',
    borderColor: 'border-blue-200 dark:border-blue-500/20',
  },
  {
    key: 'hermes',
    name: 'Hermes',
    vendor: 'Nous Research',
    tools: [{ key: 'hermes-agent', name: 'Hermes Agent' }],
    color: { light: '#f59e0b', dark: '#d97706' },
    bgLight: 'bg-amber-50 dark:bg-amber-500/10',
    textColor: 'text-amber-600 dark:text-amber-400',
    borderColor: 'border-amber-200 dark:border-amber-500/20',
  },
  {
    key: 'copilot',
    name: 'Copilot',
    vendor: 'Microsoft',
    tools: [{ key: 'microsoft-copilot', name: 'Microsoft Copilot' }],
    color: { light: '#0078d4', dark: '#0078d4' },
    bgLight: 'bg-sky-50 dark:bg-sky-500/10',
    textColor: 'text-sky-600 dark:text-sky-400',
    borderColor: 'border-sky-200 dark:border-sky-500/20',
  },
  {
    key: 'grok',
    name: 'Grok',
    vendor: 'xAI',
    tools: [
      { key: 'grok', name: 'Grok' },
      { key: 'xai-api', name: 'xAI API' },
    ],
    color: { light: '#f43f5e', dark: '#f43f5e' },
    bgLight: 'bg-rose-50 dark:bg-rose-500/10',
    textColor: 'text-rose-600 dark:text-rose-400',
    borderColor: 'border-rose-200 dark:border-rose-500/20',
  },
  {
    key: 'perplexity',
    name: 'Perplexity',
    vendor: 'Perplexity',
    tools: [{ key: 'perplexity', name: 'Perplexity' }],
    color: { light: '#06b6d4', dark: '#0891b2' },
    bgLight: 'bg-cyan-50 dark:bg-cyan-500/10',
    textColor: 'text-cyan-600 dark:text-cyan-400',
    borderColor: 'border-cyan-200 dark:border-cyan-500/20',
  },
];

export const providerColor = (provider: AiProvider, isDark: boolean) =>
  isDark ? provider.color.dark : provider.color.light;

/** Mix a hex color toward white (amount > 0) or black (amount < 0). */
function mixHex(hex: string, amount: number): string {
  const clean = hex.replace('#', '');
  const target = amount >= 0 ? 255 : 0;
  const t = Math.min(Math.abs(amount), 1);
  const channel = (i: number) => {
    const v = parseInt(clean.slice(i, i + 2), 16);
    return Math.round(v + (target - v) * t)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${channel(0)}${channel(2)}${channel(4)}`;
}

/**
 * Shade steps of the provider hue for its tools (Chat / Code / Cowork /
 * Files …): base, darker, lighter, deeper — a same-ramp sequence so tool
 * identity stays inside the provider's color while the legend + table carry
 * the exact mapping.
 */
export const toolColor = (provider: AiProvider, toolIndex: number, isDark: boolean): string => {
  const base = providerColor(provider, isDark);
  const steps = isDark ? [0, 0.28, -0.25, 0.52] : [0, -0.3, 0.4, -0.52];
  return mixHex(base, steps[toolIndex % steps.length]);
};
